import React from 'react';
import { motion } from 'framer-motion';
import { Plane, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';

const AirFreight = () => {
  const services = [
    {
      title: 'Express Air Freight',
      description: 'Next-flight-out and priority boarding for urgent shipments',
      features: ['Door-to-door service', 'Real-time tracking', 'Customs clearance', 'Insurance options'],
    },
    {
      title: 'Standard Air Freight',
      description: 'Cost-effective solutions for less urgent cargo',
      features: ['Global network access', 'Consolidation services', 'Weekly schedules', 'Competitive rates'],
    },
    {
      title: 'Charter Services',
      description: 'Dedicated aircraft for oversized or ultra-urgent cargo',
      features: ['Any airport worldwide', 'Flexible departure times', 'Maximum payload', 'Direct routing'],
    },
  ];

  const benefits = [
    'Access to all major airlines and routes',
    'Dangerous goods certified (IATA/ICAO)',
    'Temperature-controlled solutions',
    'High-value cargo security',
    'Customs brokerage expertise',
    'Proactive delay management',
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Global Air Freight Solutions | Aero Time Critical" description="Express and standard air freight, airport-to-airport and door-to-door, direct and consolidated services with customs coordination and next-flight-out options." path="/air-freight" />
      <PageHero
        badge="GLOBAL CONNECTIONS"
        BadgeIcon={Plane}
        title="Air Freight"
        titleAccent="Solutions"
        description="Fast, reliable air cargo services connecting you to every continent with priority routing and expert handling for time-sensitive shipments."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/dsr13j63_airfreight%20pic%202.png"
      />

      {/* Services */}
      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="service-card"
              >
                <h3 className="text-2xl font-bold mb-4 text-[#003057]">{service.title}</h3>
                <p className="text-[#4A4A4A] mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="text-[#0089D0] flex-shrink-0" size={20} />
                      <span className="text-[#003057]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Advantages</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-8 text-[#003057]">
                Why Choose Our Air Freight?
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 p-4 bg-[#F7F9FB] rounded-lg border-l-4 border-[#0089D0]"
                  >
                    <CheckCircle className="text-[#0089D0] flex-shrink-0" size={22} />
                    <span className="text-[#003057] font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/dsr13j63_airfreight%20pic%202.png"
                alt="Aero Time Critical branded cargo aircraft over a connected global network"
                className="w-full h-[480px] object-cover rounded-lg shadow-xl"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Ship?"
        description="Get a competitive quote for your air freight shipment today"
        buttonText="Request Air Freight Quote"
      />
    </div>
  );
};

export default AirFreight;
